import { useEffect, useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import { Paperclip, Download, Trash2, Upload, FileText } from "lucide-react";
import CrmPanel from "./CrmPanel";
import {
  crmListReceipts, crmCreateReceipt, crmUpdateReceipt, crmDeleteReceipt,
  crmUploadReceiptFile, crmDeleteReceiptFile, crmReceiptFileUrl,
  adminListClients, crmList,
} from "@/lib/api";
import { INR, fmtDate } from "./crmUtils";

const ACCEPT_MIME = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";
const MAX_MB = 5;

export default function PaymentReceipts({ token }) {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [totals, setTotals] = useState({ count: 0, amount: 0 });
  const fileInputRef = useRef(null);
  const [pendingRow, setPendingRow] = useState(null); // row being (re)uploaded

  useEffect(() => {
    adminListClients(token).then(setClients).catch(() => {});
    crmList(token, "projects").then(setProjects).catch(() => {});
  }, [token]);

  const clientMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c.name || c.dashboard_name || c.username])),
    [clients],
  );

  const fields = [
    { name: "received_date", label: "Payment received date", type: "date", required: true },
    { name: "client_id",     label: "Received from client",  type: "select", required: true,
      options: [{ value: "", label: "— Select client —" }, ...clients.map((c) => ({ value: c.id, label: c.name || c.dashboard_name || c.username }))] },
    { name: "payment_mode",  label: "Payment mode",          type: "select", required: true,
      options: [
        { value: "Bank Transfer", label: "Bank Transfer" },
        { value: "UPI",           label: "UPI" },
        { value: "Cheque",        label: "Cheque" },
        { value: "Cash",          label: "Cash" },
        { value: "NEFT/RTGS",     label: "NEFT / RTGS" },
      ] },
    { name: "reference_number", label: "Reference / Transaction no.", type: "text",
      placeholder: "UTR / Cheque # / UPI txn id (optional)" },
    { name: "bank_account_name", label: "Received bank account name", type: "text", required: true, full: true,
      placeholder: "e.g. HDFC Current Account — Reachvel" },
    { name: "amount", label: "Amount (₹)", type: "money", required: true },
    { name: "notes",  label: "Notes",      type: "textarea", full: true, rows: 3,
      placeholder: "Any note that should appear on the receipt (optional)" },
  ];

  const triggerUpload = (row) => {
    setPendingRow(row);
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-picked
    if (!file || !pendingRow) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large (max ${MAX_MB} MB)`);
      setPendingRow(null);
      return;
    }
    try {
      await crmUploadReceiptFile(token, pendingRow.id, file);
      toast.success("Receipt attachment uploaded");
      window.dispatchEvent(new Event("crm-payment-receipts-refresh"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setPendingRow(null);
    }
  };

  const removeAttachment = async (row) => {
    if (!window.confirm(`Remove attached receipt from record dated ${fmtDate(row.received_date)}?`)) return;
    try {
      await crmDeleteReceiptFile(token, row.id);
      toast.success("Attachment removed");
      window.dispatchEvent(new Event("crm-payment-receipts-refresh"));
    } catch {
      toast.error("Failed to remove attachment");
    }
  };

  const downloadAutoPdf = async (row) => {
    try {
      const url = `${(process.env.REACT_APP_BACKEND_URL || "")}/api/admin/crm/payment-receipts/${row.id}/pdf`;
      const res = await fetch(url, { headers: { "X-Admin-Token": token } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Receipt_${row.reference_number || row.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Receipt PDF downloaded");
    } catch (err) {
      toast.error("Failed to generate receipt PDF");
    }
  };

  const columns = [
    { key: "received_date", label: "Received date", render: (r) => <span className="font-mono text-xs">{fmtDate(r.received_date)}</span> },
    { key: "client_id", label: "Received from client", render: (r) => (
      <span className="font-semibold text-[#0a0a0a]">{r.client_name || clientMap[r.client_id] || "—"}</span>
    ) },
    { key: "bank_account_name", label: "Bank account", render: (r) => r.bank_account_name || "—" },
    { key: "amount", label: "Amount", render: (r) => (
      <span className="font-mono font-bold text-emerald-700">{INR(r.amount)}</span>
    ) },
    { key: "pdf", label: "Auto PDF", render: (r) => (
      <button onClick={() => downloadAutoPdf(r)} data-testid={`receipt-pdf-${r.id}`}
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-[0.1em] border border-[#ff5722]/30 text-[#ff5722] rounded-full hover:bg-[#ff5722] hover:text-white transition-colors">
        <Download className="h-3 w-3" /> PDF
      </button>
    )},
    { key: "attachment", label: "Manual upload", render: (r) => (
      <div className="flex items-center gap-1.5">
        {r.has_attachment ? (
          <>
            <a href={crmReceiptFileUrl(r.id) + `?t=${encodeURIComponent(token)}`}
               target="_blank" rel="noopener noreferrer"
               onClick={(e) => {
                 // Use fetch with header to avoid token-in-url; opens blob in new tab
                 e.preventDefault();
                 fetch(crmReceiptFileUrl(r.id), { headers: { "X-Admin-Token": token } })
                   .then((res) => res.blob())
                   .then((blob) => {
                     const url = URL.createObjectURL(blob);
                     window.open(url, "_blank");
                     setTimeout(() => URL.revokeObjectURL(url), 60_000);
                   })
                   .catch(() => toast.error("Couldn't open file"));
               }}
               className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-[0.1em] border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]"
               data-testid={`receipt-view-${r.id}`}>
              <Download className="h-3 w-3" /> View
            </a>
            <button onClick={() => triggerUpload(r)}
              title="Replace file"
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-[0.1em] border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]"
              data-testid={`receipt-replace-${r.id}`}>
              <Upload className="h-3 w-3" />
            </button>
            <button onClick={() => removeAttachment(r)}
              title="Remove file"
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-[0.1em] border border-rose-300 text-rose-700 rounded-full hover:bg-rose-50"
              data-testid={`receipt-remove-file-${r.id}`}>
              <Trash2 className="h-3 w-3" />
            </button>
          </>
        ) : (
          <button onClick={() => triggerUpload(r)}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-[0.1em] border border-dashed border-black/25 text-[#4a4a4a] rounded-full hover:border-[#ff5722] hover:text-[#ff5722]"
            data-testid={`receipt-upload-${r.id}`}>
            <Paperclip className="h-3 w-3" /> Upload
          </button>
        )}
      </div>
    )},
  ];

  // Totals summary refreshed on every list load
  const wrapList = async (params) => {
    const rows = await crmListReceipts(token, params);
    setTotals({
      count: rows.length,
      amount: rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    });
    // attach sequential S.no index via row index (rendered via render(_, i))
    return rows;
  };

  return (
    <div data-testid="crm-payment-receipts-page">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">Payment Receipts</div>
          <h2 className="mt-1 crm-h text-3xl md:text-4xl text-[#0a0a0a]">
            Client payment receipts
          </h2>
          <p className="mt-2 text-sm text-[#4a4a4a]">
            Every receipt gets logged here and shows up on the client&rsquo;s portal (behind their finance password).
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border border-black/10 mb-6">
        <div className="bg-white p-5 relative overflow-hidden">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
            <FileText className="h-3.5 w-3.5" /> Total receipts
          </div>
          <div className="mt-2 text-2xl md:text-3xl font-bold text-[#0a0a0a]">{totals.count}</div>
          <span className="absolute top-0 left-0 h-1 w-full bg-blue-500" />
        </div>
        <div className="bg-white p-5 relative overflow-hidden">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
            <Paperclip className="h-3.5 w-3.5" /> Total amount
          </div>
          <div className="mt-2 text-2xl md:text-3xl font-bold text-emerald-700">{INR(totals.amount)}</div>
          <span className="absolute top-0 left-0 h-1 w-full bg-emerald-500" />
        </div>
      </div>

      {/* Hidden file input used by row buttons */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_MIME}
        style={{ display: "none" }}
        onChange={handleFileChange}
        data-testid="receipt-file-input"
      />

      <CrmPanel
        title="Payment receipts"
        entityName="payment-receipts"
        fields={fields}
        columns={columns}
        list={wrapList}
        create={(p) => crmCreateReceipt(token, p)}
        update={(id, p) => crmUpdateReceipt(token, id, p)}
        remove={(id) => crmDeleteReceipt(token, id)}
        filters={[
          { key: "client_id", label: "Client",
            options: clients.map((c) => ({ k: c.id, label: c.name || c.dashboard_name || c.username })) },
        ]}
        initialForm={{
          received_date: new Date().toISOString().slice(0, 10),
          payment_mode: "Bank Transfer",
        }}
        searchable={false}
        emptyText="No receipts logged yet. Click + New to add the first one."
      />

      {/* projects is intentionally unused for now — kept for future receipt→project linking */}
      {projects.length === 0 && null}
    </div>
  );
}
